import { useSelector } from 'react-redux';

import { useMutation, useQuery, type UseQueryOptions } from '@tanstack/react-query';
import invariant from 'tiny-invariant';

import type { FuscaFleetAddress, FuscaFleetAddressType, FuscaFleetInfo, User, UUID } from '@cobliteam/common-types';

import { UserRequester } from '@cobliteam/api-lib';
import FuscaFleetRequester from '@cobliteam/api-lib/src/dashboard/fleet';

import { Environment } from '@cobliteam/react-environments';

import type { AppState } from '@cobliteam/store';

const env = Environment.getInstance().getEnvironment();
const userRequester = new UserRequester({ host: env.herbieApiHost });
const fuscaFleetRequester = new FuscaFleetRequester({ host: env.apiHost });

const QUERY_KEY = 'FLEET';

const useFleet = () => {
  const session = useSelector((state: AppState) => state.login.session);
  const currentFleetId = session?.fleet.id;

  const getFleetId = (): UUID => {
    invariant(currentFleetId, 'Fleet id is not defined');
    return currentFleetId;
  };

  const getFleetName = (): string | undefined => {
    return session?.fleet.name;
  };
  const getFleetUsers = (options?: Partial<UseQueryOptions<User[]>>) => {
    return useQuery<User[]>({
      queryKey: [QUERY_KEY, currentFleetId],
      queryFn: () => userRequester.getFleetUsers(getFleetId()),
      staleTime: Number.POSITIVE_INFINITY,
      ...options,
    });
  };

  return {
    getFleetId,
    getFleetName,
    getFleetUsers,
  };
};

const useGetFleetInfo = (fleetId: UUID, addressType?: FuscaFleetAddressType) => {
  return useQuery<FuscaFleetInfo>({
    queryKey: [QUERY_KEY, 'INFO', fleetId, addressType],
    queryFn: () => fuscaFleetRequester.getFleetInfo(fleetId, addressType),
  });
};

const useCreateFleetAddress = (fleetId: UUID) => {
  return useMutation({
    mutationFn: (info: FuscaFleetAddress) => fuscaFleetRequester.createFleetAddress(fleetId, info),
  });
};

const useUpdateFleetAddress = (fleetId: UUID) => {
  return useMutation({
    mutationFn: (info: FuscaFleetAddress) => fuscaFleetRequester.updateFleetAddress(fleetId, info),
  });
};

export { QUERY_KEY };
export default useFleet;
export { useGetFleetInfo, useUpdateFleetAddress, useCreateFleetAddress };
